import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Spinner } from '@easydent/shared-ui';
import {
  getBillingProfile, getExports, requestExport, downloadExport,
  getClosure, requestClosure, cancelClosure,
  type ImagingExport, type ImagingClosureState,
} from '@/lib/api';

/**
 * Your data, and your way out.
 *
 * ⚠️ The honest part of this screen is what closing does NOT do. An imaging centre owns strikingly
 * little: referrals, studies, partner terms and every image it ever uploaded belong to the
 * *referring clinic*, stored in that clinic's tenant under that clinic's retention obligation. A
 * centre that assumed "close my account" removes its uploads from clinics would be wrong in a way
 * that matters, so the page says it plainly instead of letting them find out.
 *
 * ⚠️ Closing also does not suspend the centre — deliberately, and unlike a supplier whose campaigns
 * stop at once. A centre part-way through somebody's scan must be able to finish and upload it;
 * cutting delivery off at notice turns one organisation's departure into another's clinical gap,
 * and the patient is the one left without images. Open work is shown before the button for exactly
 * that reason.
 */

const dateFmt = (iso: string | null, lang: string) =>
  iso ? new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' }) : '';

const statusTone: Record<string, string> = {
  Ready: 'bg-brand-50 text-brand-ink-strong',
  Pending: 'bg-amber-50 text-amber-700',
  Running: 'bg-amber-50 text-amber-700',
  Failed: 'bg-red-50 text-red-600',
};

const AccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [centreName, setCentreName] = useState<string | null>(null);
  const [exports, setExports] = useState<ImagingExport[] | null>(null);
  const [closure, setClosure] = useState<ImagingClosureState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [showClose, setShowClose] = useState(false);

  const load = async () => {
    try {
      const [profile, rows, state] = await Promise.all([getBillingProfile(), getExports(), getClosure()]);
      setCentreName(profile.name);
      setExports(rows);
      setClosure(state);
    } catch {
      setError(t('account.loadError', 'Could not load your account details.'));
    }
  };
  useEffect(() => { load(); }, []);

  // Poll only while something is actually building, and stop as soon as it is not.
  useEffect(() => {
    const working = exports?.some((e) => e.status === 'Pending' || e.status === 'Running');
    if (!working) return;
    const id = setInterval(() => { getExports().then(setExports).catch(() => {}); }, 10_000);
    return () => clearInterval(id);
  }, [exports]);

  const onExport = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await requestExport();
      setExports(await getExports());
      setNotice(t('account.exportQueued', 'Your export is being prepared. It usually takes a few minutes — you can leave this page.'));
    } catch {
      setError(t('account.exportError', 'Could not start the export. Please try again.'));
    } finally { setBusy(false); }
  };

  const onDownload = async (id: string) => {
    setError(null);
    try { await downloadExport(id); }
    catch { setError(t('account.downloadError', 'That download link could not be created. Try again.')); }
  };

  const onClose = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      setClosure(await requestClosure(confirmation));
      setConfirmation('');
      setShowClose(false);
    } catch (err) {
      setError((err as { response?: { data?: { title?: string } } })?.response?.data?.title
        ?? t('account.closeError', 'Could not schedule the closure.'));
    } finally { setBusy(false); }
  };

  const onKeep = async () => {
    setBusy(true); setError(null);
    try {
      setClosure(await cancelClosure());
      setNotice(t('account.closeCancelled', 'Your account will stay open. Nothing has been deleted.'));
    } catch {
      setError(t('account.cancelError', 'Could not call off the closure. Please try again.'));
    } finally { setBusy(false); }
  };

  if (error && !exports) return <Alert variant="error">{error}</Alert>;
  if (!exports || !closure) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-body">{t('account.title', 'Your data and account')}</h1>

      {error && <Alert variant="error">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      {closure.isClosing && (
        <Alert variant="warning">
          <div className="space-y-3">
            <p className="font-semibold">{t('account.closingTitle', 'This centre is scheduled to close')}</p>
            <p>
              {t('account.closingBody', {
                defaultValue: 'Your centre details will be deleted on {{date}}. Until then you can change your mind, and nothing is lost.',
                date: dateFmt(closure.dataDeletedOn, i18n.language),
              })}
            </p>
            {/* The single most important sentence on the page while a closure is pending. */}
            <p className="text-sm">
              {t('account.closingStillWorking', 'You can still accept referrals and upload results in the meantime — please finish any scan already under way.')}
            </p>
            <Button onClick={onKeep} disabled={busy}>
              {t('account.keepAccount', 'Keep my account open')}
            </Button>
          </div>
        </Alert>
      )}

      {/* ── Export ────────────────────────────────────────────────────────── */}
      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="font-semibold text-body">{t('account.exportTitle', 'Download your data')}</h2>
            <p className="mt-1 text-sm text-muted">
              {t('account.exportBody', 'A ZIP archive of your centre details, wallet history and a summary of the work you have done. You can ask for one at any time.')}
            </p>
          </div>

          {/* ⚠️ Stated, not discovered. A centre sees patient names in its live worklist because it
              must know who is arriving; a permanent file that leaves our control is a different act. */}
          <p className="rounded-lg bg-canvas px-3 py-2 text-sm text-muted">
            {t('account.exportPrivacy', 'The work summary contains dates, status and urgency only — no patient names and no clinical reasons. Those belong to the clinic that referred them.')}
          </p>

          <Button onClick={onExport} disabled={busy}>
            {t('account.exportStart', 'Prepare a new export')}
          </Button>

          {exports.length === 0 ? (
            <p className="text-sm text-subtle-ink">{t('account.exportNone', 'You have not exported anything yet.')}</p>
          ) : (
            <ul className="divide-y divide-line-soft">
              {exports.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone[e.status] ?? 'bg-canvas text-muted'}`}>
                    {t(`account.status.${e.status.toLowerCase()}`, e.status)}
                  </span>
                  <span className="text-muted">{dateFmt(e.requestedAt, i18n.language)}</span>
                  {e.sizeBytes ? (
                    <span className="text-subtle-ink">{Math.max(1, Math.round(e.sizeBytes / 1024))} KB</span>
                  ) : null}
                  {e.status === 'Ready' && (
                    <Button variant="secondary" className="ml-auto" onClick={() => onDownload(e.id)}>
                      {t('account.download', 'Download')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* ── Closing ───────────────────────────────────────────────────────── */}
      {!closure.isClosing && (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold text-body">{t('account.closeTitle', 'Close this centre')}</h2>
              <p className="mt-1 text-sm text-muted">
                {t('account.closeBody', {
                  defaultValue: 'Your centre details are deleted {{days}} days from now, and until then you can change your mind.',
                  days: closure.graceDays,
                })}
              </p>
            </div>

            {/* ⚠️ The correction most worth making before somebody clicks. */}
            <div className="rounded-lg bg-canvas px-3 py-2.5 text-sm text-muted">
              <p className="font-medium text-body-soft">{t('account.keptTitle', 'What stays with the clinics')}</p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5">
                <li>{t('account.keptStudies', 'Every image and report you delivered. Those are the clinic’s medical records, held under its own retention obligation — closing your account does not remove them.')}</li>
                <li>{t('account.keptReferrals', 'The referrals and agreements themselves, which are recorded in each clinic’s books. They will show your centre as closed.')}</li>
                <li>{t('account.keptBilling', 'Invoices and the wallet transactions behind them — required by tax law.')}</li>
              </ul>
            </div>

            {/* Plural forms live in the locale files — Romanian needs three (_one/_few/_other), and
                an inline default cannot carry them. */}
            {closure.openReferrals > 0 && (
              <Alert variant="info">
                {t('account.openReferrals', { count: closure.openReferrals })}
              </Alert>
            )}

            {!showClose ? (
              <Button variant="danger" onClick={() => setShowClose(true)}>
                {t('account.closeStart', 'Close my account')}
              </Button>
            ) : (
              <form onSubmit={onClose} className="space-y-3">
                <Input
                  label={t('account.confirmLabel', {
                    defaultValue: 'Type your centre’s name — {{name}} — to confirm',
                    name: centreName ?? '',
                  })}
                  value={confirmation}
                  onChange={(ev) => setConfirmation(ev.target.value)}
                  autoComplete="off"
                />
                <div className="flex gap-2">
                  <Button type="submit" variant="danger" disabled={busy || confirmation.trim() !== (centreName ?? '')}>
                    {t('account.closeConfirm', 'Close my account')}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => { setShowClose(false); setConfirmation(''); }}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AccountPage;
